/**
 * Puff Up — risk-and-reward inflate remix of the tap-clicker formula.
 *
 * Rules
 *   - Each player has a balloon with `size` in 0..POP_THRESHOLD.
 *   - Every tap adds `TAP_BASE_SIZE * comboMult` to the local balloon.
 *   - Above DANGER_ZONE the balloon visually shakes and flashes red.
 *   - If size reaches POP_THRESHOLD the balloon POPS: size resets to 0,
 *     the player is stunned for STUN_MS, and their combo breaks.
 *   - Pumping lasts PUMP_MS. When the timer hits zero the player with
 *     the largest balloon wins. Ties are broken by fewer pops, then by
 *     most taps.
 *
 * Architecture
 *   - synchronized mode — the host owns every balloon's size and combo
 *     state, broadcasts a snapshot at ~15 Hz, and runs stun countdowns
 *     + combo decay each tick.
 *   - taps are routed as one-shot SDK events ('pu-tap'). Clients get
 *     instant local feedback (ripple + puff SFX) but don't mutate their
 *     own size optimistically — the snapshot catches up within a frame.
 *   - pop detection runs on every peer: whenever a snapshot shows a
 *     player's pop-count increased, we spawn a BIG bang + rubber scraps
 *     at their balloon.
 */
import { SdkGameScene } from '@platform/sdk';

// ─────────────────────────────────────────────────────────── tuning

// Balloon
const POP_THRESHOLD = 100;
const TAP_BASE_SIZE = 2.0;   // units added per tap at tier 0
const DANGER_ZONE   = 80;    // size at which the visual goes red+shaky
const STUN_MS       = 1200;  // how long a popped player is locked out

// Combos — same ladder as horse-dash / tap-tug so the muscle memory
// transfers between games.
const COMBO_WINDOW_MS = 360;
const COMBO_TIERS = [
  { min: 0,  mult: 1.0,  label: '',          color: 0xffffff, glow: 0x000000 },
  { min: 4,  mult: 1.25, label: 'GOOD!',     color: 0xfacc15, glow: 0xfde68a },
  { min: 8,  mult: 1.5,  label: 'GREAT!',    color: 0xfb923c, glow: 0xfed7aa },
  { min: 12, mult: 1.8,  label: 'AWESOME!',  color: 0xec4899, glow: 0xfbcfe8 },
  { min: 16, mult: 2.2,  label: 'ON FIRE!',  color: 0xef4444, glow: 0xfecaca }
];

// Phase timings (ms)
const COUNTDOWN_MS = 3200;
const PUMP_MS      = 30000;
const END_MS       = 5400;

// Phase enum
const P_COUNTDOWN = 'countdown';
const P_PUMP      = 'pump';
const P_END       = 'end';

// Per-player palette
const BALLOON_COLORS = [
  0xef4444, 0x3b82f6, 0x10b981, 0xfacc15,
  0xec4899, 0x8b5cf6, 0x06b6d4, 0xf97316
];

// HUD layout
const HUD_PILL_HEIGHT = 34;
const HUD_PILL_GAP    = 6;
const HUD_TOP         = 12;
const HUD_SIDE        = 12;

// Balloon visual
const BALLOON_MIN_R = 14;
const BALLOON_MAX_R = 64;

// ─────────────────────────────────────────────────────────── scene

export default class GameScene extends SdkGameScene {

  start({ players }) {
    this.W = this.scale.width;
    this.H = this.scale.height;

    // Player lookup tables from the roster.
    this._players = (players || this.sdk.players || []).slice();
    this._playerNames = {};
    this._playerColors = {};
    this._playerIndex = {};
    for (let i = 0; i < this._players.length; i++) {
      const p = this._players[i];
      this._playerNames[p.id] = p.name || p.id;
      this._playerColors[p.id] = BALLOON_COLORS[i % BALLOON_COLORS.length];
      this._playerIndex[p.id] = i;
    }

    // Authoritative state.
    this.state = {
      phase: P_COUNTDOWN,
      phaseAt: 0,
      phaseLen: COUNTDOWN_MS,
      players: this._initPlayers(),
      winnerId: null,
      matchOver: false
    };

    this._tapQueue = [];

    if (this.sdk.isHost) {
      this._unsubTap = this.sdk.on('pu-tap', (payload) => {
        if (payload && payload.playerId) {
          this._tapQueue.push({ playerId: payload.playerId, ts: payload.ts || Date.now() });
        }
      });
    }

    // Track per-player deltas so peers can fire pop / combo effects when
    // snapshots arrive.
    this._lastPopCount   = {};
    this._lastComboTier  = {};
    this._lastSize       = {};
    this._renderSize     = {};   // smoothed visual size
    for (const id of Object.keys(this._playerNames)) {
      this._lastPopCount[id] = 0;
      this._lastComboTier[id] = 0;
      this._lastSize[id] = 0;
      this._renderSize[id] = 0;
    }

    this._lastResizeW = this.W;
    this._lastResizeH = this.H;

    // Scene construction
    this._buildBackground();
    this._buildField();
    this._buildBalloons();
    this._buildParticleLayers();
    this._buildHud();
    this._buildOverlayText();
    this._buildEncouragement();
    this._buildTimer();

    this._setupLocalInput();
    this._startMusic();

    this.events.once('shutdown', () => this._teardown());
  }

  _initPlayers() {
    const out = {};
    for (const p of this._players) {
      out[p.id] = {
        size: 0,
        combo: 0,
        comboTier: 0,
        lastTapAt: 0,
        taps: 0,
        stunLeft: 0,
        popCount: 0
      };
    }
    return out;
  }

  // ─────────────────────────────────────────────────── host simulation

  tick(dt, _inputs) {
    if (!this.sdk.isHost) return;
    if (this.state.matchOver) return;

    const dtMs = dt * 1000;
    this.state.phaseAt += dtMs;
    const nowMs = Date.now();

    // Stun countdown + combo decay (only during PUMP).
    if (this.state.phase === P_PUMP) {
      for (const id of Object.keys(this.state.players)) {
        const p = this.state.players[id];
        if (p.stunLeft > 0) {
          p.stunLeft = Math.max(0, p.stunLeft - dtMs);
        }
        if (p.combo > 0 && (nowMs - p.lastTapAt) > COMBO_WINDOW_MS) {
          p.combo = 0;
          p.comboTier = 0;
        }
      }

      // Drain queued taps.
      while (this._tapQueue.length) {
        const tap = this._tapQueue.shift();
        this._applyTap(tap.playerId, tap.ts);
      }
    } else {
      this._tapQueue.length = 0;
    }

    switch (this.state.phase) {
      case P_COUNTDOWN:
        if (this.state.phaseAt >= this.state.phaseLen) this._enterPhase(P_PUMP, PUMP_MS);
        break;

      case P_PUMP:
        if (this.state.phaseAt >= this.state.phaseLen) {
          this._computeWinner();
          this._enterPhase(P_END, END_MS);
        }
        break;

      case P_END:
        if (this.state.phaseAt >= this.state.phaseLen) this._finalizeMatch();
        break;
    }
  }

  _enterPhase(phase, lenMs) {
    const prev = this.state.phase;
    this.state.phase = phase;
    this.state.phaseAt = 0;
    this.state.phaseLen = lenMs;
    this._onPhaseChanged(prev, phase);
  }

  _applyTap(playerId, ts) {
    const p = this.state.players[playerId];
    if (!p) return;
    if (p.stunLeft > 0) return; // Stunned — tap ignored entirely.

    // Combo bookkeeping.
    if (ts - p.lastTapAt <= COMBO_WINDOW_MS) {
      p.combo += 1;
    } else {
      p.combo = 1;
    }
    p.lastTapAt = ts;
    p.taps += 1;

    // Resolve combo tier.
    let tier = 0;
    for (let i = COMBO_TIERS.length - 1; i >= 0; i--) {
      if (p.combo >= COMBO_TIERS[i].min) { tier = i; break; }
    }
    p.comboTier = tier;
    const mult = COMBO_TIERS[tier].mult;

    // Inflate.
    p.size += TAP_BASE_SIZE * mult;

    // Pop check.
    if (p.size >= POP_THRESHOLD) {
      p.size = 0;
      p.stunLeft = STUN_MS;
      p.popCount += 1;
      p.combo = 0;
      p.comboTier = 0;
    }
  }

  _computeWinner() {
    // Biggest balloon wins. Ties broken by (fewer pops, then more taps).
    let best = null;
    for (const id of Object.keys(this.state.players)) {
      const p = this.state.players[id];
      if (!best) {
        best = { id, size: p.size, pops: p.popCount, taps: p.taps };
        continue;
      }
      if (p.size > best.size ||
          (p.size === best.size && p.popCount < best.pops) ||
          (p.size === best.size && p.popCount === best.pops && p.taps > best.taps)) {
        best = { id, size: p.size, pops: p.popCount, taps: p.taps };
      }
    }
    this.state.winnerId = best && best.size > 0 ? best.id : (best ? best.id : null);
  }

  _finalizeMatch() {
    if (this.state.matchOver) return;
    this.state.matchOver = true;

    // GameOver score column = final balloon size (rounded).
    const scores = {};
    for (const id of Object.keys(this.state.players)) {
      scores[id] = Math.round(this.state.players[id].size);
    }
    const winnerName = this.state.winnerId
      ? (this._playerNames[this.state.winnerId] || this.state.winnerId)
      : 'tie';
    this.sdk.endMatch({ winnerName, scores });
  }

  // ─────────────────────────────────────────────────── snapshots

  snapshot() {
    const players = {};
    for (const id of Object.keys(this.state.players)) {
      const p = this.state.players[id];
      players[id] = {
        s:  round2(p.size),
        c:  p.combo,
        t:  p.comboTier,
        la: p.lastTapAt,
        tx: p.taps,
        sl: Math.round(p.stunLeft),
        px: p.popCount
      };
    }
    return {
      ph: this.state.phase,
      pa: this.state.phaseAt,
      pl: this.state.phaseLen,
      p:  players,
      w:  this.state.winnerId,
      m:  this.state.matchOver ? 1 : 0
    };
  }

  applySnapshot(snap) {
    if (!snap) return;
    const prevPhase = this.state.phase;
    this.state.phase = snap.ph;
    this.state.phaseAt = snap.pa;
    this.state.phaseLen = snap.pl;
    this.state.winnerId = snap.w || null;
    this.state.matchOver = !!snap.m;

    if (snap.p) {
      for (const id of Object.keys(snap.p)) {
        let p = this.state.players[id];
        if (!p) {
          p = { size: 0, combo: 0, comboTier: 0, lastTapAt: 0, taps: 0, stunLeft: 0, popCount: 0 };
          this.state.players[id] = p;
        }
        p.size      = snap.p[id].s;
        p.combo     = snap.p[id].c;
        p.comboTier = snap.p[id].t;
        p.lastTapAt = snap.p[id].la;
        p.taps      = snap.p[id].tx;
        p.stunLeft  = snap.p[id].sl;
        p.popCount  = snap.p[id].px;
      }
    }

    if (prevPhase !== this.state.phase) this._onPhaseChanged(prevPhase, this.state.phase);
  }

  _onPhaseChanged(prev, next) {
    if (next === P_PUMP) {
      this._playSfx('pu_go');
      this.cameras.main.shake(180, 0.006);
    } else if (next === P_END) {
      this._playSfx('pu_win');
      this._spawnFireworks();
    }
  }

  _playSfx(key) {
    if (this.sdk.audio && this.sdk.audio.playSfx) {
      try { this.sdk.audio.playSfx(key); } catch (_) { /* ignore */ }
    }
  }

  // ─────────────────────────────────────────── scene construction

  _buildBackground() {
    const W = this.W, H = this.H;
    const g = this.add.graphics().setDepth(0);

    // Bright party gradient — pink at the top, warm yellow near the ground.
    const stops = [
      { y: 0.00, color: 0x4c1d95 },
      { y: 0.30, color: 0xa855f7 },
      { y: 0.58, color: 0xf472b6 },
      { y: 0.72, color: 0xfde68a }
    ];
    for (let i = 0; i < stops.length - 1; i++) {
      g.fillStyle(stops[i].color, 1);
      g.fillRect(0, stops[i].y * H, W, (stops[i + 1].y - stops[i].y) * H + 2);
    }
    g.fillStyle(stops[stops.length - 1].color, 1);
    g.fillRect(0, stops[stops.length - 1].y * H, W, H - stops[stops.length - 1].y * H);

    // Confetti dots behind everything
    for (let i = 0; i < 30; i++) {
      const x = (i * 97.3 + 40) % W;
      const y = ((i * 53.7) % (H * 0.55)) + 30;
      const palette = [0xfde047, 0xfca5a5, 0xa5f3fc, 0xd8b4fe, 0xbbf7d0];
      g.fillStyle(palette[i % palette.length], 0.55);
      g.fillRect(x, y, 6, 3);
    }

    // Streamers in the top corners
    g.lineStyle(4, 0xfde047, 0.85);
    g.beginPath(); g.moveTo(0, 0); g.lineTo(W * 0.18, H * 0.12); g.lineTo(W * 0.05, H * 0.22); g.strokePath();
    g.lineStyle(4, 0xa5f3fc, 0.85);
    g.beginPath(); g.moveTo(W, 0); g.lineTo(W * 0.82, H * 0.12); g.lineTo(W * 0.95, H * 0.22); g.strokePath();
  }

  _buildField() {
    const W = this.W, H = this.H;

    // Ground strip where the stakes live.
    const groundTop = H * 0.78;
    const groundBottom = H - 4;
    this._groundTop = groundTop;
    this._groundBottom = groundBottom;
    this._stakeY = groundTop + 18;

    const g = this.add.graphics().setDepth(1);

    // Grass strip
    g.fillStyle(0x22c55e, 1);
    g.fillRect(0, groundTop, W, groundBottom - groundTop);
    g.fillStyle(0x16a34a, 1);
    g.fillRect(0, groundTop, W, 6);
    g.fillStyle(0x14532d, 0.5);
    g.fillRect(0, groundBottom - 6, W, 6);

    // Dotted grass blades
    for (let x = 12; x < W; x += 22) {
      g.fillStyle(0x86efac, 0.85);
      g.fillTriangle(x, groundTop + 10, x + 3, groundTop + 2, x + 6, groundTop + 10);
    }

    // Stakes for each player — small wooden posts along the ground.
    const n = this._players.length;
    this._stakeXs = {};
    for (let i = 0; i < n; i++) {
      const id = this._players[i].id;
      // Space players evenly, leaving margins.
      const tx = (i + 1) / (n + 1);
      const stakeX = W * tx;
      this._stakeXs[id] = stakeX;

      // Stake post
      g.fillStyle(0x78350f, 1);
      g.fillRect(stakeX - 2, this._stakeY - 8, 4, 18);
      g.fillStyle(0x451a03, 1);
      g.fillRect(stakeX - 2, this._stakeY - 8, 4, 3);
    }
  }

  _buildBalloons() {
    // Each balloon is a container (body + highlight + knot) drawn into a
    // Graphics that we clear and repaint every frame. The container
    // position is driven off `renderSize` so the balloon rises as it
    // grows.
    this.balloons = {};
    for (const id of Object.keys(this._playerNames)) {
      const stakeX = this._stakeXs[id];
      const c = this.add.container(stakeX, this._stakeY - 30).setDepth(20);

      // Balloon string (drawn as a separate graphics so it always runs
      // from the knot to the stake, regardless of balloon scale).
      const string = this.add.graphics().setDepth(19);
      // Balloon body graphics (drawn each frame)
      const body = this.add.graphics();
      c.add(body);

      // Name tag below the stake
      const name = (this._playerNames[id] || '').slice(0, 9);
      const label = this.add.text(stakeX, this._stakeY + 16, name, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '12px',
        fontStyle: 'bold',
        color: '#ffffff',
        stroke: '#000000',
        strokeThickness: 3
      }).setOrigin(0.5, 0).setDepth(21);

      // Local player gets a small crown above the name.
      let crown = null;
      if (id === this.sdk.localPlayerId) {
        crown = this.add.text(stakeX, this._stakeY + 14, '★', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '14px',
          color: '#fde047'
        }).setOrigin(0.5, 1).setDepth(21);
      }

      this.balloons[id] = {
        container: c,
        body,
        string,
        label,
        crown,
        color: this._playerColors[id],
        stakeX,
        baseY: this._stakeY - 30,
        visible: true,
        shakePhase: Math.random() * Math.PI * 2
      };
    }
  }

  _buildParticleLayers() {
    this.fxGraphics = this.add.graphics().setDepth(15);
    /** @type {Array<object>} */
    this._particles = [];
  }

  _buildHud() {
    const W = this.W;
    this.hudContainer = this.add.container(0, 0).setDepth(100);
    this.hudRows = {};

    const ids = Object.keys(this._playerNames);
    const n = ids.length;

    const twoCols = n > 4;
    const innerMargin = HUD_SIDE;
    const leftCount = twoCols ? Math.ceil(n / 2) : n;
    const pillWidth = twoCols
      ? (W - innerMargin * 3) / 2
      : W - innerMargin * 2;

    for (let i = 0; i < n; i++) {
      const id = ids[i];
      const isLeft = i < leftCount;
      const colIndex = isLeft ? i : (i - leftCount);
      const xOrigin = isLeft ? innerMargin : (innerMargin * 2 + pillWidth);
      const y = HUD_TOP + colIndex * (HUD_PILL_HEIGHT + HUD_PILL_GAP);
      const row = this._buildHudRow(id, xOrigin, y, pillWidth);
      this.hudContainer.add(row.group);
      this.hudRows[id] = row;
    }
  }

  _buildHudRow(playerId, originX, originY, width) {
    const color = this._playerColors[playerId] || 0xffffff;
    const name = this._playerNames[playerId] || playerId;

    const group = this.add.container(originX, originY);

    // Pill background
    const bg = this.add.graphics();
    bg.fillStyle(0x000000, 0.45);
    bg.fillRoundedRect(0, 0, width, HUD_PILL_HEIGHT, 14);
    bg.lineStyle(2, color, 0.95);
    bg.strokeRoundedRect(0, 0, width, HUD_PILL_HEIGHT, 14);
    group.add(bg);

    // Colour tag
    const tag = this.add.graphics();
    tag.fillStyle(color, 1);
    tag.fillRoundedRect(6, 6, 22, 22, 11);
    tag.lineStyle(2, 0xffffff, 0.9);
    tag.strokeRoundedRect(6, 6, 22, 22, 11);
    group.add(tag);

    // Player name
    const nameText = this.add.text(34, HUD_PILL_HEIGHT / 2, String(name).slice(0, 8), {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '13px',
      fontStyle: 'bold',
      color: '#ffffff'
    }).setOrigin(0, 0.5);
    group.add(nameText);

    // Size bar (with danger-zone marker) in the middle stretchy area.
    const barLeft = 98;
    const barRight = width - 68;
    const barWidth = Math.max(20, barRight - barLeft);
    const barH = 12;
    const barY = (HUD_PILL_HEIGHT - barH) / 2;

    const barTrack = this.add.graphics();
    barTrack.fillStyle(0xffffff, 0.18);
    barTrack.fillRoundedRect(barLeft, barY, barWidth, barH, 6);
    // Danger marker — a red tick at the DANGER_ZONE boundary.
    const dangerX = barLeft + barWidth * (DANGER_ZONE / POP_THRESHOLD);
    barTrack.lineStyle(2, 0xfecaca, 0.9);
    barTrack.lineBetween(dangerX, barY - 3, dangerX, barY + barH + 3);
    group.add(barTrack);

    const barFill = this.add.graphics();
    group.add(barFill);

    // Right: "x12  82" — combo count and balloon size, right aligned.
    const comboText = this.add.text(width - 40, HUD_PILL_HEIGHT / 2, 'x0', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '12px',
      fontStyle: 'bold',
      color: '#ffffff'
    }).setOrigin(1, 0.5);
    group.add(comboText);

    const sizeText = this.add.text(width - 8, HUD_PILL_HEIGHT / 2, '0', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '13px',
      fontStyle: 'bold',
      color: '#ffffff'
    }).setOrigin(1, 0.5);
    group.add(sizeText);

    return {
      group, barTrack, barFill, comboText, sizeText, color,
      barLeft, barWidth, barH, barY,
      shownCombo: 0,
      shownSize: 0
    };
  }

  _renderHud() {
    if (!this.hudRows) return;
    for (const id of Object.keys(this.hudRows)) {
      const row = this.hudRows[id];
      const p = this.state.players[id];
      if (!p) continue;

      // Size bar — colour shifts red when in the danger zone.
      const size = Math.max(0, Math.min(POP_THRESHOLD, p.size));
      const frac = size / POP_THRESHOLD;
      const fillW = row.barWidth * frac;
      const inDanger = size >= DANGER_ZONE;
      const fillColor = inDanger ? 0xef4444 : row.color;

      row.barFill.clear();
      row.barFill.fillStyle(fillColor, 1);
      row.barFill.fillRoundedRect(row.barLeft, row.barY, Math.max(0, fillW), row.barH, 6);
      row.barFill.fillStyle(0xffffff, 0.35);
      row.barFill.fillRect(row.barLeft, row.barY + 2, Math.max(0, fillW), 2);

      // Size readout
      const sizeInt = Math.round(size);
      if (sizeInt !== row.shownSize) {
        row.shownSize = sizeInt;
        row.sizeText.setText(String(sizeInt));
        row.sizeText.setColor(inDanger ? '#fecaca' : '#ffffff');
      }

      // Combo readout — pulse on change.
      if (p.combo !== row.shownCombo) {
        row.shownCombo = p.combo;
        row.comboText.setText('x' + p.combo);
        const tier = COMBO_TIERS[p.comboTier] || COMBO_TIERS[0];
        row.comboText.setColor('#' + (tier.color === 0xffffff ? 'ffffff' : tier.color.toString(16).padStart(6, '0')));
        if (p.combo > 0) {
          this.tweens.killTweensOf(row.comboText);
          row.comboText.setScale(1.4);
          this.tweens.add({
            targets: row.comboText,
            scale: 1,
            duration: 220,
            ease: 'Back.out'
          });
        }
      }
    }
  }

  _buildOverlayText() {
    const W = this.W, H = this.H;

    this.statusText = this.add.text(W / 2, H * 0.30, '', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '30px',
      fontStyle: 'bold',
      color: '#ffffff',
      stroke: '#0f172a',
      strokeThickness: 6,
      align: 'center'
    }).setOrigin(0.5).setDepth(200);

    this.countdownText = this.add.text(W / 2, H * 0.42, '', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '160px',
      fontStyle: 'bold',
      color: '#ffffff',
      stroke: '#dc2626',
      strokeThickness: 12
    }).setOrigin(0.5).setDepth(300);

    this.instructionsText = this.add.text(W / 2, H - 14,
      'TAP TO PUFF — STOP BEFORE IT POPS!', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '13px',
      fontStyle: 'bold',
      color: '#ffffff',
      stroke: '#000000',
      strokeThickness: 3
    }).setOrigin(0.5, 1).setDepth(200);
  }

  _buildEncouragement() {
    this.encourageText = this.add.text(this.W / 2, this.H * 0.50, '', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '64px',
      fontStyle: 'bold',
      color: '#ffffff',
      stroke: '#000000',
      strokeThickness: 8,
      align: 'center'
    }).setOrigin(0.5).setDepth(310).setAlpha(0);
  }

  _buildTimer() {
    // Countdown clock pinned to the top centre during the pump phase.
    const W = this.W;
    const n = Object.keys(this._playerNames).length;
    const twoCols = n > 4;
    const rows = twoCols ? Math.ceil(n / 2) : n;
    const hudBottom = HUD_TOP + rows * (HUD_PILL_HEIGHT + HUD_PILL_GAP);
    this.timerText = this.add.text(W / 2, hudBottom + 8, '', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '20px',
      fontStyle: 'bold',
      color: '#ffffff',
      stroke: '#0f172a',
      strokeThickness: 5
    }).setOrigin(0.5, 0).setDepth(101);
  }

  /** Rebuild the whole scene when the viewport changes. */
  onResize(width, height) {
    if (!width || !height) return;
    if (width === this._lastResizeW && height === this._lastResizeH) return;
    this._lastResizeW = width;
    this._lastResizeH = height;
    this.W = width;
    this.H = height;

    this.children.removeAll(true);
    this._particles = [];

    this._buildBackground();
    this._buildField();
    this._buildBalloons();
    this._buildParticleLayers();
    this._buildHud();
    this._buildOverlayText();
    this._buildEncouragement();
    this._buildTimer();
  }

  // ─────────────────────────────────────────── per-frame rendering

  update(time, delta) {
    // Clients smooth the phase timer between snapshots so animations
    // progress without waiting for the next host message.
    if (!this.sdk.isHost && !this.state.matchOver) {
      this.state.phaseAt += delta;
    }
    const dt = delta / 1000;

    this._detectPopTransitions();
    this._detectComboTransitions();
    this._renderHud();
    this._renderBalloons(dt);
    this._renderCountdown();
    this._renderStatusText();
    this._renderTimer();
    this._updateParticles(dt);
  }

  _detectPopTransitions() {
    // If a player's popCount increased since the last frame, a pop just
    // happened on the host. Fire a BANG on every peer so the bang stays
    // in sync with what you see on screen.
    for (const id of Object.keys(this.state.players)) {
      const p = this.state.players[id];
      const last = this._lastPopCount[id] || 0;
      if (p.popCount > last) {
        this._onBalloonPop(id);
      }
      this._lastPopCount[id] = p.popCount;
      this._lastSize[id] = p.size;
    }
  }

  _onBalloonPop(playerId) {
    const balloon = this.balloons[playerId];
    if (!balloon) return;

    // Spawn the scrap particles at the LAST rendered position of the
    // balloon (before the size reset snaps it down).
    const cy = balloon.container.y;
    const cx = balloon.container.x;
    this._spawnPopScraps(cx, cy, balloon.color);

    // Audio + screen feedback
    this._playSfx('pu_pop');
    if (playerId === this.sdk.localPlayerId) {
      this.cameras.main.shake(260, 0.014);
      this._showEncouragement('POP!', 0xef4444);
    } else {
      this.cameras.main.shake(130, 0.005);
    }
  }

  _detectComboTransitions() {
    for (const id of Object.keys(this.state.players)) {
      const p = this.state.players[id];
      const last = this._lastComboTier[id] || 0;
      if (p.comboTier > last) this._onComboLevelUp(id, p.comboTier);
      this._lastComboTier[id] = p.comboTier;
    }
  }

  _onComboLevelUp(playerId, tierIdx) {
    const tier = COMBO_TIERS[tierIdx];
    if (!tier || tierIdx === 0) return;

    if (playerId === this.sdk.localPlayerId) {
      this._showEncouragement(tier.label, tier.color);
      this._playSfx(tierIdx >= 3 ? 'pu_cheer' : 'pu_combo');
      this.cameras.main.shake(120 + tierIdx * 30, 0.004 + tierIdx * 0.001);
    } else {
      this._playSfx('pu_combo');
    }

    const balloon = this.balloons[playerId];
    if (balloon) {
      this._spawnComboBurst(balloon.container.x, balloon.container.y, tier);
    }
  }

  _renderBalloons(dt) {
    const nowMs = Date.now();
    for (const id of Object.keys(this.balloons)) {
      const balloon = this.balloons[id];
      const p = this.state.players[id];
      if (!p) continue;

      // Smooth the rendered size toward the authoritative number so
      // the balloon grows visibly rather than snapping to the new
      // snapshot value on every message.
      const targetSize = Math.max(0, Math.min(POP_THRESHOLD, p.size));
      const lerp = Math.min(1, dt * 12);
      this._renderSize[id] += (targetSize - this._renderSize[id]) * lerp;
      const renderSize = this._renderSize[id];

      const stunned = p.stunLeft > 0;

      // Radius + position — balloon rises as it grows.
      const sizeFrac = renderSize / POP_THRESHOLD;
      const r = BALLOON_MIN_R + (BALLOON_MAX_R - BALLOON_MIN_R) * sizeFrac;
      const cx = balloon.stakeX;
      const cy = balloon.baseY - (BALLOON_MAX_R - BALLOON_MIN_R) * sizeFrac;

      // Danger-zone shake — magnitude scales with how deep you are.
      let shakeX = 0, shakeY = 0;
      if (renderSize >= DANGER_ZONE) {
        const deep = (renderSize - DANGER_ZONE) / Math.max(1, POP_THRESHOLD - DANGER_ZONE);
        const amp = 1 + deep * 4;
        balloon.shakePhase += dt * 22;
        shakeX = Math.sin(balloon.shakePhase) * amp;
        shakeY = Math.cos(balloon.shakePhase * 1.3) * amp;
      }

      balloon.container.x = cx + shakeX;
      balloon.container.y = cy + shakeY;

      // Balloon body colour — shift toward red when deep in the danger
      // zone to make the warning unmissable even without audio.
      const dangerT = renderSize > DANGER_ZONE
        ? Math.min(1, (renderSize - DANGER_ZONE) / (POP_THRESHOLD - DANGER_ZONE))
        : 0;
      const bodyColor = dangerT > 0
        ? lerpColor(balloon.color, 0xef4444, dangerT * 0.75)
        : balloon.color;

      const body = balloon.body;
      body.clear();

      if (stunned) {
        // Stunned state — draw a small deflated scrap sitting on the
        // stake so the player knows they're locked out for a moment.
        const deflatedR = 8 + Math.sin(nowMs * 0.015) * 1.5;
        body.fillStyle(balloon.color, 0.55);
        body.fillEllipse(0, BALLOON_MAX_R - 10, deflatedR * 2, deflatedR);
        body.lineStyle(1, 0x000000, 0.7);
        body.strokeEllipse(0, BALLOON_MAX_R - 10, deflatedR * 2, deflatedR);
      } else if (renderSize > 0.5) {
        drawBalloon(body, 0, 0, r, bodyColor);
      }

      // String: from the balloon knot to the stake top.
      const s = balloon.string;
      s.clear();
      if (!stunned && renderSize > 0.5) {
        const knotX = cx + shakeX;
        const knotY = cy + shakeY + r * 1.05 + 5;
        s.lineStyle(1.5, 0xe5e7eb, 0.85);
        s.beginPath();
        s.moveTo(knotX, knotY);
        // Slight curve
        s.lineTo(balloon.stakeX, this._stakeY);
        s.strokePath();
      }
    }
  }

  _showEncouragement(text, color) {
    if (!this.encourageText) return;
    this.encourageText.setText(text);
    this.encourageText.setColor('#' + color.toString(16).padStart(6, '0'));
    this.encourageText.setStroke('#000000', 8);
    this.tweens.killTweensOf(this.encourageText);
    this.encourageText.setAlpha(1);
    this.encourageText.setScale(1.6);
    this.encourageText.y = this.H * 0.50;
    this.tweens.add({
      targets: this.encourageText,
      scale: 1,
      duration: 220,
      ease: 'Back.out'
    });
    this.tweens.add({
      targets: this.encourageText,
      y: this.H * 0.42,
      alpha: 0,
      duration: 700,
      delay: 380,
      ease: 'Cubic.in'
    });
  }

  _renderCountdown() {
    if (this.state.phase !== P_COUNTDOWN) {
      if (this.countdownText.visible) this.countdownText.setVisible(false);
      return;
    }
    const remainingMs = Math.max(0, this.state.phaseLen - this.state.phaseAt);
    const remaining = Math.ceil(remainingMs / 1000);
    const txt = remaining > 0 ? String(remaining) : 'GO!';
    if (this.countdownText.text !== txt) {
      this.countdownText.setText(txt);
      this.tweens.killTweensOf(this.countdownText);
      this.countdownText.setScale(1.5);
      this.countdownText.alpha = 0;
      this.tweens.add({
        targets: this.countdownText,
        scale: 1,
        alpha: 1,
        duration: 280,
        ease: 'Cubic.out'
      });
      this._playSfx(remaining > 0 ? 'pu_tick' : 'pu_go');
    }
    this.countdownText.setVisible(true);
  }

  _renderStatusText() {
    let msg = '';
    if (this.state.phase === P_COUNTDOWN) {
      msg = 'GET READY TO PUFF!';
    } else if (this.state.phase === P_PUMP) {
      msg = 'TAP TO PUFF UP!';
    } else if (this.state.phase === P_END) {
      const wn = this.state.winnerId
        ? (this._playerNames[this.state.winnerId] || this.state.winnerId)
        : '';
      msg = wn ? (wn + ' WINS! 🏆') : "TIME'S UP!";
    }

    if (this.statusText.text !== msg) {
      this.statusText.setText(msg);
      if (msg) {
        this.tweens.killTweensOf(this.statusText);
        this.statusText.setScale(1.4);
        this.tweens.add({
          targets: this.statusText,
          scale: 1,
          duration: 260,
          ease: 'Back.out'
        });
      }
    }
  }

  _renderTimer() {
    if (!this.timerText) return;
    if (this.state.phase !== P_PUMP) {
      this.timerText.setVisible(false);
      return;
    }
    const remainingMs = Math.max(0, this.state.phaseLen - this.state.phaseAt);
    const seconds = Math.max(0, Math.ceil(remainingMs / 1000));
    const txt = seconds + 's';
    if (this.timerText.text !== txt) this.timerText.setText(txt);
    // Turn the timer red in the final 5 seconds for urgency.
    this.timerText.setColor(seconds <= 5 ? '#fecaca' : '#ffffff');
    this.timerText.setVisible(true);
  }

  // ─────────────────────────────────────────────────── particles

  _spawnPopScraps(x, y, baseColor) {
    // Big ring of coloured rubber scraps
    for (let i = 0; i < 28; i++) {
      const a = (i / 28) * Math.PI * 2;
      const sp = 180 + Math.random() * 160;
      this._particles.push({
        x, y,
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp - 40,
        color: baseColor,
        shape: 'rect',
        size: 5 + Math.random() * 4,
        life: 0, ttl: 0.9,
        rot: Math.random() * Math.PI * 2,
        vrot: (Math.random() - 0.5) * 8,
        gravity: 480
      });
    }
    // White shock ring
    for (let i = 0; i < 14; i++) {
      const a = (i / 14) * Math.PI * 2;
      const sp = 260 + Math.random() * 60;
      this._particles.push({
        x, y,
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp,
        color: 0xffffff,
        shape: 'sparkle',
        size: 4, life: 0, ttl: 0.45,
        rot: 0, vrot: 0, gravity: 0
      });
    }
  }

  _spawnComboBurst(x, y, tier) {
    const color = tier ? tier.color : 0xfacc15;
    for (let i = 0; i < 16; i++) {
      const a = (i / 16) * Math.PI * 2;
      const sp = 120 + Math.random() * 70;
      this._particles.push({
        x, y,
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp,
        color,
        shape: 'circle',
        size: 4 + Math.random() * 3,
        life: 0, ttl: 0.6,
        rot: 0, vrot: 0, gravity: 60
      });
    }
    for (let i = 0; i < 10; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = 60 + Math.random() * 90;
      this._particles.push({
        x, y,
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp - 30,
        color: 0xffffff,
        shape: 'sparkle',
        size: 3,
        life: 0, ttl: 0.55,
        rot: 0, vrot: 0, gravity: 0
      });
    }
  }

  _spawnLocalPuffDust(playerId) {
    const balloon = this.balloons[playerId];
    if (!balloon) return;
    const x = balloon.container.x;
    const y = balloon.container.y;
    const color = balloon.color;
    for (let i = 0; i < 4; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = 30 + Math.random() * 40;
      this._particles.push({
        x, y,
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp - 10,
        color,
        shape: 'cloud',
        size: 4 + Math.random() * 3,
        life: 0, ttl: 0.4,
        rot: 0, vrot: 0, gravity: -10
      });
    }
  }

  _spawnFireworks() {
    const W = this.W, H = this.H;
    const colors = [0xef4444, 0xfacc15, 0x3b82f6, 0x10b981, 0xec4899, 0x8b5cf6];
    for (let b = 0; b < 5; b++) {
      const cx = W * (0.15 + Math.random() * 0.7);
      const cy = H * (0.20 + Math.random() * 0.25);
      const color = colors[b % colors.length];
      this.time.delayedCall(b * 320, () => {
        for (let i = 0; i < 38; i++) {
          const a = (i / 38) * Math.PI * 2;
          const sp = 200 + Math.random() * 120;
          this._particles.push({
            x: cx, y: cy,
            vx: Math.cos(a) * sp, vy: Math.sin(a) * sp,
            color, shape: 'circle',
            size: 4, life: 0, ttl: 1.4,
            rot: 0, vrot: 0, gravity: 180
          });
        }
        this._playSfx('pu_tick');
      });
    }
  }

  _spawnTapRipple(x, y, color = 0xffffff, alpha = 0.9) {
    const ring = this.add.graphics().setDepth(14);
    ring.lineStyle(4, color, alpha);
    ring.strokeCircle(0, 0, 18);
    ring.x = x;
    ring.y = y;
    this.tweens.add({
      targets: ring,
      scaleX: 2.4,
      scaleY: 2.4,
      alpha: 0,
      duration: 380,
      ease: 'Cubic.out',
      onComplete: () => ring.destroy()
    });
  }

  _updateParticles(dt) {
    const g = this.fxGraphics;
    g.clear();
    for (let i = this._particles.length - 1; i >= 0; i--) {
      const p = this._particles[i];
      p.life += dt;
      if (p.life >= p.ttl) { this._particles.splice(i, 1); continue; }
      p.vy += p.gravity * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.rot += p.vrot * dt;
      const a = 1 - (p.life / p.ttl);

      if (p.shape === 'circle') {
        g.fillStyle(p.color, a);
        g.fillCircle(p.x, p.y, p.size * (0.3 + a * 0.7));
      } else if (p.shape === 'rect') {
        g.fillStyle(p.color, a);
        g.fillRect(p.x - p.size / 2, p.y - p.size / 3, p.size, p.size * 0.6);
      } else if (p.shape === 'cloud') {
        g.fillStyle(p.color, a * 0.55);
        g.fillCircle(p.x, p.y, p.size * (1 + p.life * 0.8));
      } else if (p.shape === 'sparkle') {
        g.fillStyle(0xffffff, a);
        g.fillCircle(p.x, p.y, p.size * a);
        g.lineStyle(2 * a, 0xffffff, a * 0.8);
        g.strokeCircle(p.x, p.y, p.size * 2 + (1 - a) * 5);
      }
    }
  }

  // ─────────────────────────────────────────────────── local input

  _setupLocalInput() {
    const onTap = (pointer) => {
      if (this.state.matchOver) return;
      if (this.state.phase !== P_PUMP) {
        if (pointer) this._spawnTapRipple(pointer.x, pointer.y, 0xffffff, 0.4);
        return;
      }
      // Check for stun on the client side so tap feedback matches the
      // authoritative behaviour. If the host is about to unstun us in
      // this tick our tap will be ignored either way — better to show
      // nothing than a tap that "eats" while stunned.
      const p = this.state.players[this.sdk.localPlayerId];
      if (p && p.stunLeft > 0) {
        if (pointer) this._spawnTapRipple(pointer.x, pointer.y, 0xfecaca, 0.45);
        return;
      }

      this._fireLocalTap();
      if (pointer) {
        const col = this._playerColors[this.sdk.localPlayerId] || 0xffffff;
        this._spawnTapRipple(pointer.x, pointer.y, col, 0.95);
      }
    };

    this.input.on('pointerdown', onTap);

    if (this.input.keyboard) {
      this.input.keyboard.on('keydown-SPACE', () => onTap(null));
      this.input.keyboard.on('keydown-ENTER', () => onTap(null));
    }
  }

  _fireLocalTap() {
    const playerId = this.sdk.localPlayerId;
    const payload = { playerId, ts: Date.now() };
    if (this.sdk.isHost) {
      this._tapQueue.push(payload);
    } else {
      this.sdk.emit('pu-tap', payload);
    }
    // Immediate local feedback — the snapshot catches the balloon up
    // within a frame, so we don't optimistically mutate size here.
    this._playSfx('pu_puff');
    this._spawnLocalPuffDust(playerId);
  }

  // ─────────────────────────────────────────────────── music

  _startMusic() {
    if (this.sdk.audio && this.sdk.audio.playMusic) {
      try { this.sdk.audio.playMusic('pu_music'); } catch (_) { /* ignore */ }
    }
  }

  _teardown() {
    try { if (this.sdk.audio && this.sdk.audio.stopMusic) this.sdk.audio.stopMusic(); } catch (_) {}
    try { if (this._unsubTap) this._unsubTap(); } catch (_) {}
  }
}

// ───────────────────────────────────────────────────────── helpers

function round2(v) { return Math.round(v * 100) / 100; }

/** Draw a cartoon balloon body (with highlight + knot) into Graphics. */
function drawBalloon(g, cx, cy, r, color) {
  // Body (slightly taller than wide)
  g.fillStyle(color, 1);
  g.fillEllipse(cx, cy, r * 2, r * 2.2);
  // Outline
  g.lineStyle(2, 0x000000, 0.35);
  g.strokeEllipse(cx, cy, r * 2, r * 2.2);
  // Big soft highlight (upper-left)
  g.fillStyle(0xffffff, 0.45);
  g.fillEllipse(cx - r * 0.36, cy - r * 0.42, r * 0.5, r * 0.7);
  // Small bright highlight
  g.fillStyle(0xffffff, 0.85);
  g.fillCircle(cx - r * 0.42, cy - r * 0.55, r * 0.12);
  // Knot at the bottom
  g.fillStyle(color, 1);
  g.beginPath();
  g.moveTo(cx - 3, cy + r * 1.07);
  g.lineTo(cx + 3, cy + r * 1.07);
  g.lineTo(cx + 2, cy + r * 1.07 + 5);
  g.lineTo(cx - 2, cy + r * 1.07 + 5);
  g.closePath();
  g.fillPath();
  g.lineStyle(1, 0x000000, 0.45);
  g.strokePath();
}

/** Linear interpolate between two 0xRRGGBB values, t in [0,1]. */
function lerpColor(a, b, t) {
  const ar = (a >> 16) & 0xff, ag = (a >> 8) & 0xff, ab = a & 0xff;
  const br = (b >> 16) & 0xff, bg = (b >> 8) & 0xff, bb = b & 0xff;
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  return (r << 16) | (g << 8) | bl;
}
